import {
  BiotechOutlined,
  ContentCopyOutlined,
  FileDownloadOutlined,
  HubOutlined,
  Inventory2Outlined,
  MoreHoriz,
  RefreshOutlined,
  SaveOutlined,
  Undo,
} from '@mui/icons-material'
import {
  Alert,
  AlertTitle,
  alpha,
  Box,
  CircularProgress,
  Divider,
  IconButton,
  Menu,
  MenuItem,
  Stack,
  Typography,
  useTheme,
} from '@mui/material'
import { useQueryClient } from '@tanstack/react-query'
import { ReactNode, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'

import type { TCloneDialogOptions } from '~/features/campaigns/components/cloneCampaignDialog/types'

import CustomDialog from '~/components/customDialog'
import useGetAgentCentralized from '~/features/agents/hooks/useGetAgentCentralized'
import CampaignShareDialog from '~/features/campaigns/components/campaignShareDialog'
import CloneCampaignDialog from '~/features/campaigns/components/cloneCampaignDialog'
import {
  buildEnableAssistantsPayload,
  getOrderedAgentMetadataVariables,
} from '~/features/campaigns/components/ofuManageCampaign/campaignAssistants/utils'
import CampaignCreateTemplateFromCampaign from '~/features/campaigns/components/ofuManageCampaign/campaignCreateTemplateFromCampaign'
import useArchiveCampaigns from '~/features/campaigns/hooks/useArchiveCampaigns'
import useCloneOfuCampaign from '~/features/campaigns/hooks/useCloneOfuCampaign'
import useIsAdmin from '~/features/campaigns/hooks/useIsAdmin'
import useUpdateCampaign from '~/features/campaigns/hooks/useUpdateCampaign'
import {
  ECampaignAgentJunctionStatus,
  ECampaignExecutionStatus,
  ECampaignKeys,
  ECampaignListItemKeys,
  ECampaignAgentAccessScope,
  ECampaignStatus,
  ECampaignType,
  TCampaign,
} from '~/features/campaigns/models'
import { getCampaignChannels, queryKeys } from '~/features/campaigns/utils'
import {
  getCampaignJunctions,
  getNonSharedCloneOriginalAssistantId,
} from '~/features/campaigns/utils/campaignJunctions'
import { EReportDashboard } from '~/features/reports/models'
import { getReportsPathForDashboard } from '~/features/reports/utils'
import useGetSelectedCampaign from '~/hooks/useGetSelectedCampaign'
import rem from '~/utils/ui'

const DEFAULT_CLONE_NAME_SUFFIX = '-Copy'
const TEST_STATUS_TOAST_TEXT = 'Campaign marked as test successfully'

type TExtraAction = {
  key: string
  label: string
  icon: ReactNode
  onClick: () => void
  disabled?: boolean
  render?: (onClick: () => void) => ReactNode
}

export default function CampaignMoreActions({
  extraActions = [],
}: {
  extraActions?: TExtraAction[]
}) {
  const theme = useTheme()
  const queryClient = useQueryClient()
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const [isCreatingTemplate, setIsCreatingTemplate] = useState(false)
  const [isArchivingCampaign, setIsArchivingCampaign] = useState(false)
  const [isMarkAsTestDialogOpen, setIsMarkAsTestDialogOpen] = useState(false)
  const [isCloneDialogOpen, setIsCloneDialogOpen] = useState(false)
  const [isShareDialogOpen, setIsShareDialogOpen] = useState(false)
  const [cloneCampaignName, setCloneCampaignName] = useState('')
  const open = Boolean(anchorEl)

  const isAdmin = useIsAdmin()
  const { campaign, isArchived, isDraft, isTest, isSharedCampaign } =
    useGetSelectedCampaign()
  const { selectedAgent, agents } = useGetAgentCentralized()

  const agentId = campaign?.[ECampaignListItemKeys.agentId] || ''
  const currentAgentId = selectedAgent?.id || agentId
  const allAssistants = agents || []
  const showShareAction = Boolean(campaign?.[ECampaignKeys.orgId])
  const campaignJunctionAssistantIds = useMemo(
    () =>
      Array.from(
        new Set(
          getCampaignJunctions(campaign)
            .map((junction) =>
              String(junction.agentId || junction.Agent?.id || ''),
            )
            .filter(Boolean),
        ),
      ),
    [campaign],
  )
  const cloneOriginalAssistantId = useMemo(
    () =>
      getNonSharedCloneOriginalAssistantId(campaign ?? null) ||
      String(currentAgentId || ''),
    [campaign, currentAgentId],
  )
  // Every declared metadata variable, required or not — an assistant added to
  // the campaign gets every location field its record can supply.
  const agentMetadataVariables = useMemo(
    () =>
      getOrderedAgentMetadataVariables(
        campaign?.[ECampaignKeys.agentMetadataKeys],
      ),
    [campaign],
  )

  const { mutate: archiveCampaign, isPending: isArchiving } =
    useArchiveCampaigns(agentId, campaign?.status === ECampaignStatus.archived)

  const { mutate: updateCampaign, isPending: isUpdating } = useUpdateCampaign(
    campaign?.id || '',
  )

  const { cloneCampaign, isCloning } = useCloneOfuCampaign()

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget)
  }
  const navigate = useNavigate()
  const { id: routeCampaignId } = useParams()

  /**
   * The id comes from the route first, not the fetched campaign: this menu
   * renders while the campaign request is still in flight, and it survives a
   * failed request that is not a 404. Reading it off `campaign` meant the click
   * hit an early return and did nothing at all — no navigation, no feedback.
   *
   * All-time on purpose: "export this campaign" means every contact in it, not
   * the export page's default last-30-days window.
   */
  const handleExportCampaignData = () => {
    setAnchorEl(null)
    const campaignId = String(
      routeCampaignId || campaign?.[ECampaignKeys.id] || '',
    ).trim()
    if (!campaignId) {
      // Should be unreachable now the id comes from the route, but never fail
      // silently: a dead menu item with no feedback is impossible to diagnose.
      toast.error('Could not determine which campaign to export.')
      return
    }
    navigate(
      `${getReportsPathForDashboard(EReportDashboard.download)}?campaignId=${encodeURIComponent(campaignId)}&datePreset=all-time`,
    )
  }

  const handleClose = () => {
    setAnchorEl(null)
  }

  const handleExtraActionClick = (action: TExtraAction) => {
    setAnchorEl(null)
    action.onClick()
  }

  const handleSaveAsTemplate = () => {
    setIsCreatingTemplate(true)
    setAnchorEl(null)
  }

  const handleCloneCampaignClick = () => {
    setAnchorEl(null)
    const defaultName = campaign?.[ECampaignKeys.campaignName]
      ? `${campaign.campaignName}${DEFAULT_CLONE_NAME_SUFFIX}`
      : ''
    setCloneCampaignName(defaultName)
    setIsCloneDialogOpen(true)
  }

  const handleCloneDialogClose = () => {
    setIsCloneDialogOpen(false)
    setCloneCampaignName('')
  }

  const handleCloneCampaignConfirm = async (
    cloneOptions: TCloneDialogOptions,
  ) => {
    if (!campaign) {
      toast.error('Campaign data is not available')
      return
    }

    await cloneCampaign({
      campaign: campaign as TCampaign,
      cloneCampaignName,
      currentAgentId,
      isOfu:
        campaign.campaignType === ECampaignType.ofu ||
        campaign.campaignType === ECampaignType.ofuOneTime,
      cloneOptions,
      onSuccess: () => {
        handleCloneDialogClose()
      },
    })
  }

  const handleArchiveCampaign = () => {
    archiveCampaign(
      [{ id: campaign?.id || '', agentId: campaign?.agentId || '' }],
      {
        onSuccess: () => {
          toast.success(
            `Campaign ${isArchived ? 'restored' : 'archived'} successfully`,
          )
          setIsArchivingCampaign(false)
        },
        onError: () => {
          toast.error(
            `Failed to ${isArchived ? 'restore' : 'archive'} campaign. Please try again.`,
          )
        },
      },
    )
  }

  const handleArchiveCampaignClick = () => {
    setIsArchivingCampaign(true)
    setAnchorEl(null)
  }

  const handleCancelArchiveClick = () => {
    setIsArchivingCampaign(false)
  }

  const handleRevertToDraft = () => {
    setAnchorEl(null)
    const junctionAgentIds = getCampaignJunctions(campaign)
      .map((junction) => String(junction.agentId || junction.Agent?.id || ''))
      .filter(Boolean)

    const fallbackAgentId = String(
      campaign?.[ECampaignKeys.agentId] || '',
    ).trim()

    let agentIdsToRevert = junctionAgentIds

    if (junctionAgentIds.length === 0) {
      agentIdsToRevert = fallbackAgentId ? [fallbackAgentId] : []
    }

    const agentsToRevert = agentIdsToRevert.map((junctionAgentId) => ({
      agentId: junctionAgentId,
      status: ECampaignAgentJunctionStatus.notStarted,
    }))

    updateCampaign(
      {
        [ECampaignKeys.executionStatus]: ECampaignExecutionStatus.notStarted,
        [ECampaignKeys.agents]: agentsToRevert,
      },
      {
        onSuccess: () => {
          toast.success('Campaign reverted to draft successfully')
        },
        onError: () => {
          toast.error('Failed to revert campaign to draft')
        },
        onSettled: () => {
          setAnchorEl(null)
        },
      },
    )
  }

  const handleMarkAsTestClick = () => {
    setAnchorEl(null)
    setIsMarkAsTestDialogOpen(true)
  }

  const handleOpenShareDialog = () => {
    setAnchorEl(null)
    setIsShareDialogOpen(true)
  }

  const handleCloseShareDialog = () => {
    setIsShareDialogOpen(false)
  }

  const handleSaveShareSettings = async ({
    agentAccessScope,
    agentIds,
  }: {
    agentAccessScope: ECampaignAgentAccessScope
    agentIds: string[]
  }) => {
    const existingAgentIds = new Set(campaignJunctionAssistantIds.map(String))
    const newlyEnabledAssistantIds = Array.from(
      new Set(agentIds.map(String).filter(Boolean)),
    ).filter((assistantId) => !existingAgentIds.has(assistantId))
    const newAgentsPayload = buildEnableAssistantsPayload({
      assistantIds: newlyEnabledAssistantIds,
      assistants: allAssistants,
      metadataVariables: agentMetadataVariables,
      sharedCampaignAgentSettings:
        campaign?.[ECampaignKeys.sharedCampaignAgentSettings],
    })

    updateCampaign(
      {
        [ECampaignKeys.agentAccessScope]: agentAccessScope,
        ...(newAgentsPayload.length
          ? { [ECampaignKeys.agents]: newAgentsPayload }
          : {}),
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({
            queryKey: queryKeys.campaignsBase(String(currentAgentId || '')),
          })
          toast.success('Campaign sharing updated successfully')
          handleCloseShareDialog()
        },
        onError: () => {
          toast.error('Failed to update campaign sharing. Please try again.')
        },
      },
    )
  }

  const handleCancelMarkAsTest = () => {
    setIsMarkAsTestDialogOpen(false)
  }

  const handleMarkAsTestConfirm = () => {
    setAnchorEl(null)
    updateCampaign(
      {
        [ECampaignKeys.status]: ECampaignStatus.test,
      },
      {
        onSuccess: () => {
          toast.success(TEST_STATUS_TOAST_TEXT)
          setIsMarkAsTestDialogOpen(false)
        },
        onError: () => {
          toast.error('Failed to mark campaign as test')
        },
        onSettled: () => {
          setAnchorEl(null)
        },
      },
    )
  }

  return (
    <>
      <IconButton
        sx={{
          width: rem(32),
          height: rem(32),
          border: `${rem(1)} solid ${theme.palette.secondaryButtonFont}`,
          borderRadius: rem(8),
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: theme.palette.secondaryButtonFont,
          p: rem(4),
          '&:hover': {
            backgroundColor: 'transparent',
            color: theme.palette.tertiary1,
            border: `${rem(1)} solid ${theme.palette.tertiary1}`,
            '& svg': { fill: theme.palette.tertiary1 },
          },
          '&:disabled': {
            color: theme.palette.tertiary5,
            border: `${rem(1)} solid ${theme.palette.tertiary5}`,
          },
        }}
        onClick={handleClick}
      >
        {isUpdating || isArchiving ? (
          <CircularProgress size={20} />
        ) : (
          <MoreHoriz />
        )}
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        sx={{
          p: 0,
          '.MuiPaper-root': { borderRadius: rem(8), p: 1 },
          '& .MuiMenu-list': { p: 0 },
          '& .MuiMenuItem-root': {
            p: 1,
            borderRadius: rem(8),
            '&:hover': {
              backgroundColor: alpha(theme.palette.primaryButtonBg, 0.5),
            },
          },
        }}
      >
        {extraActions.map((action) =>
          action.render ? (
            <Box key={action.key} sx={{ mb: 0.5 }}>
              {action.render(() => handleExtraActionClick(action))}
            </Box>
          ) : (
            <MenuItem
              key={action.key}
              onClick={() => handleExtraActionClick(action)}
              disabled={action.disabled}
            >
              {action.icon}
              <Typography>{action.label}</Typography>
            </MenuItem>
          ),
        )}
        {extraActions.length > 0 && <Divider />}

        {isAdmin && (
          <MenuItem onClick={handleSaveAsTemplate}>
            <SaveOutlined fontSize="small" sx={{ mr: 2 }} />
            <Typography>Save as template</Typography>
          </MenuItem>
        )}

        {isAdmin && !isDraft && (
          <MenuItem onClick={handleRevertToDraft}>
            <Undo fontSize="small" sx={{ mr: 2 }} />
            <Typography>Revert to draft</Typography>
          </MenuItem>
        )}

        <MenuItem onClick={handleCloneCampaignClick}>
          <ContentCopyOutlined fontSize="small" sx={{ mr: 2 }} />
          <Typography>Clone Campaign</Typography>
        </MenuItem>

        <MenuItem onClick={handleExportCampaignData}>
          <FileDownloadOutlined fontSize="small" sx={{ mr: 2 }} />
          <Typography>Export campaign data</Typography>
        </MenuItem>

        {showShareAction && (
          <MenuItem onClick={handleOpenShareDialog}>
            <HubOutlined fontSize="small" sx={{ mr: 2 }} />
            <Typography>Share Campaign</Typography>
          </MenuItem>
        )}

        {isAdmin && !isTest && !isArchived && (
          <MenuItem onClick={handleMarkAsTestClick}>
            <BiotechOutlined fontSize="small" sx={{ mr: 2 }} />
            <Typography>Mark as test</Typography>
          </MenuItem>
        )}

        <Divider />
        {campaign?.status !== ECampaignStatus.archived ? (
          <MenuItem onClick={handleArchiveCampaignClick}>
            <Inventory2Outlined fontSize="small" sx={{ mr: 2 }} />
            <Typography>Archive Campaign</Typography>
          </MenuItem>
        ) : (
          <MenuItem onClick={handleArchiveCampaignClick}>
            <RefreshOutlined fontSize="small" sx={{ mr: 2 }} />
            <Typography>Restore Campaign</Typography>
          </MenuItem>
        )}
      </Menu>

      <CampaignCreateTemplateFromCampaign
        isOpen={isCreatingTemplate}
        setIsOpen={setIsCreatingTemplate}
      />

      <CloneCampaignDialog
        isOpen={isCloneDialogOpen}
        onClose={handleCloneDialogClose}
        onCancel={handleCloneDialogClose}
        isSubmitting={isCloning}
        onConfirm={handleCloneCampaignConfirm}
        cloneCampaignName={cloneCampaignName}
        onCloneCampaignNameChange={setCloneCampaignName}
        isSharedCampaign={Boolean(isSharedCampaign)}
        assistants={allAssistants}
        currentAgentId={String(currentAgentId || '')}
        originalAssistantId={cloneOriginalAssistantId}
        campaignChannels={getCampaignChannels(campaign)}
        sharedAssistantIds={campaignJunctionAssistantIds}
        placeholder={`${campaign?.[ECampaignKeys.campaignName] ?? ''}${DEFAULT_CLONE_NAME_SUFFIX}`}
      />

      <CampaignShareDialog
        isOpen={isShareDialogOpen}
        onClose={handleCloseShareDialog}
        onSave={handleSaveShareSettings}
        isSubmitting={isUpdating}
        isSharedCampaign={Boolean(isSharedCampaign)}
        assistants={allAssistants}
        currentAgentId={String(currentAgentId || '')}
        selectedCampaignAssistantIds={campaignJunctionAssistantIds}
        campaignChannels={getCampaignChannels(campaign)}
      />

      <CustomDialog
        isOpen={isMarkAsTestDialogOpen}
        onClose={handleCancelMarkAsTest}
        onCancel={handleCancelMarkAsTest}
        title="Mark campaign as test"
        description=""
        isDisabled={isUpdating}
        onConfirm={handleMarkAsTestConfirm}
        cancelText="Cancel"
        confirmText="Mark as test"
      >
        <Stack gap={1}>
          <Typography variant="body1">
            Are you sure you want to mark this campaign as test campaign?
          </Typography>

          <Typography variant="body2">
            Marking the campaign as test campaign will keep all the campaign
            functionalities but will not send any outreach to the contacts.
          </Typography>
          <Box sx={{ mt: 1, borderRadius: rem(8), overflow: 'hidden' }}>
            <Alert severity="warning">
              <AlertTitle>This action cannot be undone.</AlertTitle>
            </Alert>
          </Box>
        </Stack>
      </CustomDialog>

      <CustomDialog
        isOpen={isArchivingCampaign}
        onClose={handleCancelArchiveClick}
        onCancel={handleCancelArchiveClick}
        title={isArchived ? 'Restore Campaign' : 'Archive Campaign'}
        description=""
        isDisabled={isArchiving}
        onConfirm={handleArchiveCampaign}
        cancelText="Cancel"
        confirmText={isArchived ? 'Restore' : 'Archive'}
      >
        <Typography variant="body2">
          Are you sure you want to{' '}
          {campaign?.status === ECampaignStatus.archived
            ? 'restore'
            : 'archive'}{' '}
          this campaign?
        </Typography>
      </CustomDialog>
    </>
  )
}
